import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaChevronRight } from 'react-icons/fa';
import Icon from '../../../components/ui/Icon';
import logoUrl from '../../../assets/images/logo.svg';
import courierImg from '../../../assets/images/service-courier.webp';
import airportImg from '../../../assets/images/service-airport.webp';
import doorToDoorImg from '../../../assets/images/testimonials-bg.webp';
import packingImg from '../../../assets/images/courier-boxes.webp';
import { ScrollText, Reveal, Stagger, StaggerItem } from '../../../components/motion';
import Eyebrow from '../../../components/ui/Eyebrow';
import styles from './ServicesSection.module.css';

type Service = {
  image: string;
  title: string;
  subtitle?: string;
  description: string;
  /** Services page slug this card links to (e.g. `/services/courier`). */
  slug: string;
  /** Bento slot: the tall red card, or the wide one under the pair. */
  slot?: 'big' | 'wide';
};

const SERVICES: Service[] = [
  {
    title: 'Courier Services',
    description:
      'Domestic and international courier from Saudi Arabia. Documents and parcels are picked up from your address and tracked all the way to delivery.',
    slug: 'courier',
    image: courierImg,
    slot: 'big',
  },
  {
    title: 'Airport To Airport',
    subtitle: 'Air Cargo from Saudi Arabia',
    description:
      'Air cargo to the destination airport, with import customs cleared by us. The recipient collects from the airport cargo terminal.',
    slug: 'airport',
    image: airportImg,
  },
  {
    title: 'Door To Door',
    subtitle: 'By Air & Sea Freight',
    description:
      "We collect from your address in Saudi Arabia and deliver to the recipient's door in India, the Philippines, Bangladesh and more, with customs handled.",
    slug: 'door-to-door',
    image: doorToDoorImg,
  },
  {
    title: 'Packing And Shipping',
    description:
      'Professional cargo packing: strong boxes, wrapping and strapping. Every box is photographed and numbered before it ships by air or sea.',
    slug: 'packing-and-shipping',
    image: packingImg,
    slot: 'wide',
  },
];

/**
 * Our services — a photo grid: Courier as the tall red card, Airport and
 * Door to Door side by side, Packing wide underneath. Every photo carries the
 * Sahari logo badge and the whole card links to its service page.
 */
const ServicesSection: React.FC = () => {
  return (
    <section className={styles.services}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.head}>
          <Eyebrow>Our services</Eyebrow>
          <ScrollText as="h2" className={styles.title} accentWords={[2, 3]} accentClassName={styles.accent}>
            Cargo Services We Provide
          </ScrollText>
          <Reveal as="p" className={styles.description} delay={0.05}>
            From a single document to a full household shipment, Sahari Cargo ships from Saudi Arabia by air and
            sea: courier, air cargo, door-to-door delivery and professional packing, from 12 branches across the
            Kingdom. Every box is numbered and tracked, and we handle customs, not your recipient.
          </Reveal>
        </div>

        <Stagger as="div" className={styles.bento} amount={0.15}>
          {SERVICES.map((service, i) => (
            <StaggerItem
              key={service.slug}
              as="div"
              direction="fade"
              className={`${styles.card} ${service.slot ? styles[service.slot] : ''}`}
            >
              <Link to={`/services/${service.slug}`} className={styles.link}>
                <img src={service.image} alt="" className={styles.photo} loading="lazy" />
                <span className={styles.badge}>
                  <img src={logoUrl} alt="Sahari Cargo" />
                </span>
                <div className={styles.body}>
                  <span className={styles.num}>
                    {String(i + 1).padStart(2, '0')}
                    {service.subtitle && ` · ${service.subtitle}`}
                  </span>
                  <h3 className={styles.cardTitle}>{service.title}</h3>
                  <span className={styles.cardText}>{service.description}</span>
                  <span className={styles.more}>
                    LEARN MORE <Icon icon={FaChevronRight} />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </Container>
    </section>
  );
};

export default ServicesSection;
